import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import './LeaguesPage.css';
import { supabase, isSupabaseConfigured } from '../services/supabase';
import { leagueDateDisplay } from '../utils/dateUtils';
import { LEAGUE_TYPE, QL_ARRAY, TRAD_ARRAY } from '../constants/leagueTypes';

interface LeagueFromDB {
  id: string;
  name: string;
  tier: number;
  status: string;
  is_ai_run?: boolean;
  entry_fee?: number;
  league_type?: string;
  season_year?: number;
  sim_date?: string | null;
  max_teams?: number;
  sim_speed?: number;
}

interface TeamFromDB {
  id: string;
  name: string;
  city: string;
  abbreviation: string;
  tier: number;
  is_ai: boolean;
  league_id: string;
  conference: string;
  division: string;
}

interface StandingFromDB {
  team_id: string;
  wins: number;
  losses: number;
  division: string;
  conference: string;
  league_id: string;
}

// Track when each league was last ticked (for live speed display)
interface LeagueTickInfo {
  lastDay: number;
  lastTickTime: number;
  realSecondsPerTick: number | null;
}

const LeaguesPage: React.FC = () => {
  const navigate = useNavigate();
  const [leagues, setLeagues] = useState<LeagueFromDB[]>([]);
  const [teams, setTeams] = useState<TeamFromDB[]>([]);
  const [standings, setStandings] = useState<Map<string, StandingFromDB[]>>(new Map());
  const [loading, setLoading] = useState(true);
  const [tickInfo, setTickInfo] = useState<Map<string, LeagueTickInfo>>(new Map());
  const pollRef = useRef<NodeJS.Timeout | null>(null);
  const prevDaysRef = useRef<Map<string, number>>(new Map());

  useEffect(() => { 
    loadData();
    // Poll standings every 30s to keep records current
    pollRef.current = setInterval(loadStandingsOnly, 30000);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, []);

  async function loadData() {
    setLoading(true);
    if (isSupabaseConfigured() && supabase) {
      // Only load traditional leagues (both 'regular' and 'traditional' types)
      const { data: leagueData } = await supabase
        .from('leagues')
        .select('*')
        .in('league_type', TRAD_ARRAY as readonly string[])
        .neq('hidden', true)
        .order('tier', { ascending: true });
      if (leagueData) {
        setLeagues(leagueData as LeagueFromDB[]);
        // Initialize tick tracking
        const now = Date.now();
        const info = new Map<string, LeagueTickInfo>();
        (leagueData as LeagueFromDB[]).forEach(l => {
          info.set(l.id, { lastDay: 0, lastTickTime: now, realSecondsPerTick: null });
          prevDaysRef.current.set(l.id, 0);
        });
        setTickInfo(info);
      }

      // Only fetch teams belonging to loaded leagues — avoids 1000-row PostgREST cap
      const leagueIds = (leagueData || []).map((l: any) => l.id);
      const { data: teamData } = leagueIds.length > 0
        ? await supabase.from('teams').select('id, name, city, abbreviation, tier, is_ai, league_id, conference, division').in('league_id', leagueIds)
        : { data: [] };
      if (teamData) setTeams(teamData as TeamFromDB[]);

      await loadStandingsForLeagues(leagueData || []);
    }
    setLoading(false);
  }

  async function loadStandingsOnly() {
    if (!isSupabaseConfigured() || !supabase || leagues.length === 0) return;
    // Update tick speed by polling current_day
    await updateTickSpeeds();
    await loadStandingsForLeagues(leagues);
  }

  async function updateTickSpeeds() {
    if (leagues.length === 0) return;
    const leagueIds = leagues.map(l => l.id);
    const { data: freshData } = await supabase
      .from('leagues')
      .select('id, current_day')
      .in('id', leagueIds);
    if (!freshData) return;

    const now = Date.now();
    setTickInfo(prev => {
      const next = new Map(prev);
      (freshData as any[]).forEach(l => {
        const prevDay = prevDaysRef.current.get(l.id) || 0;
        const info = next.get(l.id) || { lastDay: 0, lastTickTime: now, realSecondsPerTick: null };
        if (l.current_day !== prevDay && prevDay > 0) {
          // Day changed — calculate real seconds per tick
          const elapsed = (now - info.lastTickTime) / 1000;
          if (elapsed > 0 && elapsed < 3600) {
            info.realSecondsPerTick = Math.round(elapsed);
          }
          info.lastTickTime = now;
        }
        info.lastDay = l.current_day;
        next.set(l.id, info);
        prevDaysRef.current.set(l.id, l.current_day);
      });
      return next;
    });
  }

  async function loadStandingsForLeagues(leagueData: any[]) {
    if (leagueData.length === 0) return;
    // Load standings filtered by each league's season_year
    const allStandings: StandingFromDB[] = [];
    for (const league of leagueData) {
      const year = league.season_year || new Date().getFullYear();
      const { data } = await supabase
        .from('league_standings')
        .select('team_id, wins, losses, division, conference, league_id')
        .eq('league_id', league.id)
        .eq('season_year', year);
      if (data) allStandings.push(...(data as StandingFromDB[]));
    }

    // Group by league_id
    const grouped = new Map<string, StandingFromDB[]>();
    allStandings.forEach(s => {
      if (!grouped.has(s.league_id)) grouped.set(s.league_id, []);
      grouped.get(s.league_id)!.push(s);
    });
    setStandings(grouped);
  }

  function getLeagueTeams(leagueId: string) {
    return teams.filter(t => t.league_id === leagueId);
  }

  // Get division leaders for a league, grouped by conference
  function getDivisionLeaders(leagueId: string): { team: TeamFromDB; standing: StandingFromDB; division: string; conference: string }[] {
    const leagueStandings = standings.get(leagueId) || [];
    const leagueTeams = getLeagueTeams(leagueId);
    const teamMap = new Map(leagueTeams.map(t => [t.id, t]));

    // Group by division (division is unique per conference)
    const byDivision = new Map<string, StandingFromDB[]>();
    const divConference = new Map<string, string>();
    leagueStandings.forEach(s => {
      const div = s.division || '???';
      const conf = s.conference || '???';
      if (!byDivision.has(div)) byDivision.set(div, []);
      byDivision.get(div)!.push(s);
      divConference.set(div, conf);
    });

    // Pick leader from each division (best record)
    const leaders: { team: TeamFromDB; standing: StandingFromDB; division: string; conference: string }[] = [];
    for (const [div, divStandings] of byDivision) {
      const sorted = [...divStandings].sort((a, b) => {
        const wpA = (a.wins + a.losses) > 0 ? a.wins / (a.wins + a.losses) : 0;
        const wpB = (b.wins + b.losses) > 0 ? b.wins / (b.wins + b.losses) : 0;
        return wpB - wpA || (b.wins - a.wins);
      });
      const leader = sorted[0];
      const team = teamMap.get(leader.team_id);
      if (team) leaders.push({ team, standing: leader, division: div, conference: divConference.get(div) || '???' });
    }
    // Sort by conference, then division
    return leaders.sort((a, b) => a.conference.localeCompare(b.conference) || a.division.localeCompare(b.division));
  }

  function getStatusLabel(status: string) {
    switch (status) {
      case 'active': return '🔴 Live';
      case 'pre_season': return '🌱 Pre-Season';
      case 'playoffs': return '🏟️ Playoffs';
      case 'off_season': return '🏁 Off-Season';
      case 'locked': return '🔒 Locked';
      case 'completed': return '✅ Complete';
      default: return status;
    }
  }

  function getSpeedLabel(leagueId: string): string {
    const info = tickInfo.get(leagueId);
    if (info?.realSecondsPerTick) {
      const secs = info.realSecondsPerTick;
      if (secs < 90) return `⚡ ${secs}s`;
      if (secs < 300) return `⏱️ ${Math.round(secs / 60)}m`;
      if (secs < 1800) return `🐢 ${Math.round(secs / 60)}m`;
      return `💤 ${Math.round(secs / 3600)}h`;
    }
    // Fallback to configured sim_speed
    const league = leagues.find(l => l.id === leagueId);
    const speed = league?.sim_speed;
    switch (speed) {
      case 1: return '⚡ 1m';
      case 2: return '🚀 2m';
      case 3: return '⏱️ 10m';
      case 4: return '🐢 30m';
      case 5: return '💤 120m';
      default: return '';
    }
  }

  function getTierLabel(tier?: number): string {
    const labels = ['', 'Premier', 'Major', 'AAA', 'AA', 'Rookie'];
    return labels[tier || 0] || `Tier ${tier}`;
  }

  if (loading) return <div className="leagues-page"><div className="loading">Loading leagues...</div></div>;

  return (
    <div className="leagues-page">
      <h1 className="leagues-title">⚾ Traditional Leagues</h1>
      <p className="leagues-sub">Full-season simulations with 24 teams, deep stats, and year-over-year progression.</p>

      <div className="league-grid">
        {leagues.length > 0 ? (
          leagues.map(league => {
            const leagueTeams = getLeagueTeams(league.id);
            const divisionLeaders = getDivisionLeaders(league.id);
            return (
              <div key={league.id} className="league-card traditional" onClick={() => navigate(`/league/${league.id}`)}>
                <div className="lc-name">{league.name}</div>
                <div className="lc-meta">
                  <span className={`lc-status ${league.status}`}>{getStatusLabel(league.status)}</span>
                  <span className="lc-teams">{leagueTeams.length} teams</span>
                  <span className="lc-year">📅 {leagueDateDisplay(league)}</span>
                  {league.status === 'active' && <span className="lc-speed">{getSpeedLabel(league.id)}</span>}
                </div>
                {divisionLeaders.length > 0 && (() => {
                  // Group leaders by conference for display
                  const byConference = new Map<string, typeof divisionLeaders>();
                  divisionLeaders.forEach(l => {
                    const conf = l.conference;
                    if (!byConference.has(conf)) byConference.set(conf, []);
                    byConference.get(conf)!.push(l);
                  });
                  return (
                    <div className="lc-standings">
                      {Array.from(byConference.entries()).map(([conference, leaders]) => (
                        <div key={conference} className="lc-conference">
                          <div className="lc-conf-header">{conference}</div>
                          {leaders.map(({ team, standing, division }) => (
                            <span key={team.id} className="lc-team">
                              <span className="lc-div">{division}</span>
                              <span className="lc-team-name">{team.name}</span>
                              <span className="lc-record">{standing.wins}-{standing.losses}</span>
                            </span>
                          ))}
                        </div>
                      ))}
                    </div>
                  );
                })()}
                {divisionLeaders.length === 0 && leagueTeams.length > 0 && (
                  <div className="lc-standings">
                    <span className="lc-team" style={{ color: '#6b7280' }}>No standings data yet</span>
                  </div>
                )}
                <div className="lc-badge trad-badge">⚾ {getTierLabel(league.tier)}</div>
              </div>
            );
          })
        ) : (
          <div className="section-empty">No traditional leagues yet</div>
        )}
      </div>
    </div>
  );
};

export default LeaguesPage;
